import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "@tanstack/react-router";
import {
  LanguageContext,
  getTranslations,
  isValidLanguage,
  LANGUAGES,
} from "@/lib/i18n";
import { setDashboardLanguageCookie } from "@/lib/dashboard-language-cookie";
import type { Language } from "@/lib/i18n";

function detectLanguageFromPath(pathname: string): Language {
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];
  if (firstSegment && isValidLanguage(firstSegment) && firstSegment !== "en") {
    return firstSegment;
  }
  return "en";
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const language = detectLanguageFromPath(location.pathname);
  const translations = useMemo(() => getTranslations(language), [language]);

  const localizedPath = useCallback(
    (path: string) => {
      if (language === "en") return path;
      // Ensure path starts with /
      const cleanPath = path.startsWith("/") ? path : `/${path}`;
      return `/${language}${cleanPath}`;
    },
    [language],
  );

  const setLanguage = useCallback(
    (newLang: Language) => {
      setDashboardLanguageCookie(newLang);

      if (newLang === language) return;

      const currentPath = location.pathname;
      // Strip existing language prefix
      let basePath = currentPath;
      const segments = currentPath.split("/").filter(Boolean);
      if (
        segments[0] &&
        isValidLanguage(segments[0]) &&
        segments[0] !== "en"
      ) {
        basePath = "/" + segments.slice(1).join("/");
        if (basePath === "/") basePath = "/";
      }
      if (!basePath.startsWith("/")) basePath = "/" + basePath;

      // Build new path
      const newPath = newLang === "en" ? basePath : `/${newLang}${basePath}`;
      navigate({ to: newPath as "/" });
    },
    [language, location.pathname, navigate],
  );

  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const hasExplicitLanguagePrefix =
      segments[0] && isValidLanguage(segments[0]) && segments[0] !== "en";

    if (hasExplicitLanguagePrefix) {
      setDashboardLanguageCookie(language);
    }
  }, [language, location.pathname]);

  // Inject hreflang and update html lang attribute
  useEffect(() => {
    if (typeof document === "undefined") return;

    // Update HTML lang
    document.documentElement.lang = language;

    // Remove existing hreflang links
    document
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((el) => el.remove());

    // Get current base path (without language prefix)
    const currentPath = location.pathname;
    const segments = currentPath.split("/").filter(Boolean);
    let basePath = currentPath;
    if (
      segments[0] &&
      isValidLanguage(segments[0]) &&
      segments[0] !== "en"
    ) {
      basePath = "/" + segments.slice(1).join("/");
    }
    if (!basePath.startsWith("/")) basePath = "/" + basePath;
    if (basePath === "") basePath = "/";

    const baseUrl = "https://zapify.pro";

    // Add hreflang links for all languages
    for (const lang of LANGUAGES) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = lang.code;
      link.href =
        lang.code === "en"
          ? `${baseUrl}${basePath}`
          : `${baseUrl}/${lang.code}${basePath}`;
      document.head.appendChild(link);
    }

    // Add x-default pointing to English
    const xDefault = document.createElement("link");
    xDefault.rel = "alternate";
    xDefault.hreflang = "x-default";
    xDefault.href = `${baseUrl}${basePath}`;
    document.head.appendChild(xDefault);

    // Update canonical
    let canonical = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href =
      language === "en"
        ? `${baseUrl}${basePath}`
        : `${baseUrl}/${language}${basePath}`;

    return () => {
      document
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((el) => el.remove());
    };
  }, [language, location.pathname]);

  return (
    <LanguageContext.Provider
      value={{ language, translations, setLanguage, localizedPath }}
    >
      {children}
    </LanguageContext.Provider>
  );
}
